import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import Preacher from "../DTOs/Preacher";
import Modal from "react-bootstrap/esm/Modal";
import Button from "react-bootstrap/esm/Button";
import Form from "react-bootstrap/esm/Form";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";

/** What the deletePreacher dry run reports (functions/src/preacherDeletion.ts). */
interface DeletionPlan {
    name: string;
    absences: number;
    accessDocs: number;
    tokens: number;
    swaps: number;
    representativeFields: string[];
    futureSlots: { serviceId: string; date: string; slot: 1 | 2 }[];
    pastSlots: number;
}

interface Props {
    gemeindeId: string | null;
    preacher: Preacher | null;
    onClose: () => void;
    onDeleted: () => void;
}

/**
 * Deleting a preacher for good (GOD-33): shows what goes with them and, if
 * they are still planned, only deletes once the admin agrees to empty those slots.
 */
export default function PreacherDeleteDialog({ gemeindeId, preacher, onClose, onDeleted }: Props) {
    const { t } = useTranslation();
    const [plan, setPlan] = useState<DeletionPlan | null>(null);
    const [clearFutureSlots, setClearFutureSlots] = useState(false);
    const [error, setError] = useState("");
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        setPlan(null);
        setClearFutureSlots(false);
        setError("");
        if (!preacher || !gemeindeId) return;
        const deletePreacher = httpsCallable<object, { plan: DeletionPlan }>(functions, "deletePreacher");
        deletePreacher({ gemeindeId, preacherKey: preacher.key, dryRun: true })
            .then((result) => setPlan(result.data.plan))
            .catch((err) => setError(err?.message ?? t("errors.anErrorOccurred")));
    }, [gemeindeId, preacher, t]);

    const needsClearing = (plan?.futureSlots.length ?? 0) > 0;
    const canDelete = !!plan && (!needsClearing || clearFutureSlots);
    const locale = t("calendar.locale") as string;
    const formatDate = (iso: string) =>
        new Date(`${iso}T12:00:00`).toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "long", year: "numeric" });

    async function handleDelete() {
        if (!preacher || !gemeindeId || !canDelete) return;
        setDeleting(true);
        setError("");
        try {
            const deletePreacher = httpsCallable(functions, "deletePreacher");
            await deletePreacher({ gemeindeId, preacherKey: preacher.key, clearFutureSlots });
            onDeleted();
        } catch (err: any) {
            setError(err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setDeleting(false);
        }
    }

    return (
        <Modal show={!!preacher} onHide={deleting ? undefined : onClose} centered>
            <Modal.Header closeButton={!deleting}>
                <Modal.Title>{t("admin.deletePreacher.title", { name: `${preacher?.name ?? ""} ${preacher?.surname ?? ""}`.trim() })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {!plan && !error && <Spinner animation="border" size="sm" />}
                {plan && (
                    <>
                        <p>{t("admin.deletePreacher.intro")}</p>
                        <ul data-testid="delete-preacher-plan">
                            <li>{t("admin.deletePreacher.absences", { count: plan.absences })}</li>
                            <li>{t("admin.deletePreacher.access", { count: plan.accessDocs })}</li>
                            <li>{t("admin.deletePreacher.tokens", { count: plan.tokens })}</li>
                            <li>{t("admin.deletePreacher.swaps", { count: plan.swaps })}</li>
                            {plan.representativeFields.length > 0 && <li>{t("admin.deletePreacher.representative")}</li>}
                        </ul>
                        <p className="small text-muted">{t("admin.deletePreacher.past", { count: plan.pastSlots })}</p>
                        {needsClearing && (
                            <Alert variant="warning">
                                <div>{t("admin.deletePreacher.stillPlanned", { count: plan.futureSlots.length })}</div>
                                <ul className="mb-2">
                                    {plan.futureSlots.map((s) => (
                                        <li key={`${s.serviceId}-${s.slot}`}>
                                            {formatDate(s.date)} · {t(s.slot === 1 ? "admin.preacher1" : "admin.preacher2")}
                                        </li>
                                    ))}
                                </ul>
                                <Form.Check
                                    id="delete-preacher-clear-slots"
                                    checked={clearFutureSlots}
                                    onChange={(e) => setClearFutureSlots(e.target.checked)}
                                    label={t("admin.deletePreacher.clearSlots")}
                                    disabled={deleting}
                                />
                            </Alert>
                        )}
                        <Alert variant="danger" className="small mb-0">
                            {t("admin.deletePreacher.irreversible")}
                        </Alert>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={deleting}>
                    {t("common.cancel")}
                </Button>
                <Button variant="danger" onClick={handleDelete} disabled={!canDelete || deleting}>
                    {deleting ? <Spinner animation="border" size="sm" /> : t("admin.deletePreacher.confirm")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
